import { useEffect, useState } from "react";

import type { CognitoUser } from "amazon-cognito-identity-js";

import { Logo } from "@alpacahq/ui/old";

import { MaintenanceBanner } from "~/v2/components/MaintenanceBanner";
import { titleCase } from "../../helpers";
import { AuthContext, AuthState, DEFAULT_AUTH_CONTEXT } from "./AuthContext";
import { Disclosure } from "./Disclosure";
import { ForgotPassword } from "./forms/ForgotPassword";
import { Login } from "./forms/Login";
import { MFA } from "./forms/MFA";
import { MFARecovery } from "./forms/MFARecovery";

export default function Auth() {
  const [authState, setAuthState] = useState<AuthState>(
    DEFAULT_AUTH_CONTEXT.authState,
  );
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | undefined>();

  useEffect(() => {
    document.title = `Alpaca - ${titleCase(authState)}`;
  }, [authState]);

  const forms = {
    [AuthState.LOGIN]: {
      title: "Login",
      subtitle: "Enter your email and password to login.",
      form: <Login />,
    },
    [AuthState.SIGNUP]: {
      title: "Sign Up",
      subtitle: "Sign up for a new account.",
      form: null, // @todo: implement signup flow
    },
    [AuthState.FORGOT_PASSWORD]: {
      title: "Forgot Password",
      subtitle: "Enter your email to reset your password.",
      form: <ForgotPassword />,
    },
    [AuthState.MFA]: {
      title: "MFA",
      subtitle: "Enter your MFA code.",
      form: <MFA />,
    },
    [AuthState.MFA_RECOVERY]: {
      title: "MFA Recovery",
      subtitle: "Enter your MFA recovery code.",
      form: <MFARecovery />,
    },
  };

  const { title, subtitle, form } = forms[authState] || {};

  return (
    <AuthContext.Provider
      value={{ authState, cognitoUser, setAuthState, setCognitoUser }}
    >
      <div className="flex min-h-screen flex-col gap-6 p-6 md:gap-28 md:py-28">
        <div className="transition-all ease-in-out">
          <div className="mx-auto flex w-full min-w-fit flex-col gap-4 transition-all ease-in-out md:w-[400px]">
            {authState === AuthState.LOGIN && <MaintenanceBanner />}
            <Logo onClick={() => setAuthState(AuthState.LOGIN)} />
            <div>
              <h1 className="mb-4 font-semibold text-xl">{title}</h1>
              <div className="text-md text-zinc-500">{subtitle}</div>
            </div>
            {form}
          </div>
        </div>
        <Disclosure className="mt-auto" />
      </div>
    </AuthContext.Provider>
  );
}
